// 模組01 人員管理 — mock 種子資料
// 四種角色、四類帳號；含審核狀態欄位（approvalStatus）。

import {
  modulesFromLevels,
  fieldsFromMasks,
  levelsFromModules,
  masksFromFields,
  PERM_KEY_TO_MODULE_KEY,
} from "../../helpers/roleContract";

// 模組01 人員管理 — mock 種子資料
// 四種角色、四類帳號；含審核狀態欄位（approvalStatus）。
// 注意:本檔不可 import RbacContext／sessionStore —— mock 啟動鏈會循環依賴。

const SYSTEM_ROLE_CODES = new Set(["Overseer", "Wrangler", "Viewer", "Gatekeeper"]);

// 角色異動歷程(角色詳情「異動歷程」段;內容比照 Figma DCK-01-01 35:3636 的範例)
const gatekeeperLog = [
  {
    at: "2026/07/07 14:32", by: "林承翰（Gatekeeper）",
    change: "機房管理　唯讀 → 可編輯", docNo: "RQ-20260707-018", result: "已核准",
  },
  {
    at: "2026/06/30 16:40", by: "林承翰（Gatekeeper・發動）",
    change: "監控派工　隱藏 → 可編輯", docNo: "RQ-20260630-009", result: "已退回",
    reason: "稽核角色不應開放監控派工編輯權（退回人：王志明 Overseer）",
  },
];

// 自訂角色的權限配置(模組級三態 + 欄位級遮罩);預設角色不存這兩個欄位,由內建能力換算
const READONLY_ALL = {
  user: "read", dashboard: "read", room: "read", registry: "read", asset: "read",
  monitor: "hidden", notify: "hidden", report: "read", decision: "hidden",
  approval: "hidden", import: "hidden",
};
const MASK_ALL = {
  contractAmount: false, assetAmount: false, reportAmount: false, decisionAmount: false,
};

// 角色列表欄位對齊 Figma DCK-01-01(48:10037):角色(名稱+描述)/類型/使用人數/最後異動/異動人/狀態
export const roles = [
  {
    id: 1, name: "Overseer", code: "Overseer", description: "老闆／大老闆",
    scope: "全域", status: "啟用中", updatedAt: "2026/06/02 14:20", updatedBy: "林承翰",
    approvalStatus: "approved",
  },
  {
    id: 2, name: "Wrangler", code: "Wrangler", description: "機房或硬體管理人員",
    scope: "機房/硬體", status: "啟用中", updatedAt: "2026/06/02 14:20", updatedBy: "林承翰",
    approvalStatus: "approved",
  },
  {
    id: 3, name: "Viewer", code: "Viewer", description: "一般人員（唯讀）",
    scope: "唯讀", status: "啟用中", updatedAt: "2026/06/02 14:20", updatedBy: "林承翰",
    approvalStatus: "approved",
  },
  {
    id: 4, name: "Gatekeeper", code: "Gatekeeper", description: "機房或硬體主管／副主管",
    scope: "審核", status: "啟用中", updatedAt: "2026/07/07 14:32", updatedBy: "林承翰",
    approvalStatus: "approved", changeLog: gatekeeperLog,
  },
  // 自訂角色(設計稿列表上的三筆):權限由 DB 指派,前端只呈現
  {
    id: 5, name: "稽核人員", code: "Auditor", description: "年度資安稽核專用（唯讀）",
    scope: "唯讀", status: "啟用中", updatedAt: "2026/06/28 10:15", updatedBy: "王志明",
    approvalStatus: "approved", levels: READONLY_ALL, masks: MASK_ALL,
  },
  {
    id: 6, name: "外包維運", code: "Outsourced", description: "駐點廠商工程師",
    scope: "機房/硬體", status: "啟用中", updatedAt: "2026/06/15 09:40", updatedBy: "林承翰",
    approvalStatus: "approved",
    levels: { ...READONLY_ALL, room: "edit", asset: "edit" },
    masks: MASK_ALL,
  },
  {
    id: 7, name: "稽核查閱", code: "AuditReader", description: "外部稽核期間臨時查閱（唯讀）",
    scope: "唯讀", status: "啟用中", updatedAt: "2026/06/28 10:15", updatedBy: "王志明",
    approvalStatus: "approved", levels: READONLY_ALL, masks: MASK_ALL,
  },
  {
    // 審核中示意(260804 稿 2128:22580 有一列黃底審核中;issue 03)——
    // 別掛在其他測試用的角色上,單獨一筆才不會凍住既有流程
    id: 8, name: "外部顧問", code: "Consultant", description: "專案期間外部顧問（唯讀）",
    scope: "唯讀", status: "啟用中", updatedAt: "2026/08/05 14:30", updatedBy: "陳雅婷",
    approvalStatus: "pending", docNo: "RQ-20260805-021", levels: READONLY_ALL, masks: MASK_ALL,
  },
];

// 使用者異動歷程(編輯頁「異動歷程」段)。
// 內容逐字比照 Figma v2 DCK-01-02 編輯頁 `2128:23509` —— 設計稿畫的就是林承翰
// (EMP-0031),所以這份掛在 id 4 身上。涵蓋設計稿列到的每一種型態:
// 一般異動、AD 聯動(免審核・一筆改兩個欄位)、多關核決、退回(含退回原因)、建立。
//
// 註:設計稿最後一筆寫的是「建立使用者：郭俊廷（EMP-0187）」,但那是別人的員編 ——
// 放在林承翰自己的歷程裡對不上,這裡改成他本人。
const chLinLog = [
  {
    at: "2026/07/07 15:20", by: "林承翰（Gatekeeper）",
    change: "綁定角色　外包維運 → 稽核查閱",
    docNo: "RQ-20260707-021", result: "已核准", source: "人工",
  },
  {
    at: "2026/07/06 03:00", by: "系統",
    // AD 聯動一次同步兩個欄位 → 一筆兩行「異動：」
    change: [
      "單位名稱　資訊處 → 資訊安全處",
      "職稱　維運工程師 → 資深維運工程師",
    ],
    source: "AD 聯動（免審核）",
  },
  {
    at: "2026/07/01 11:45", by: "林承翰（Gatekeeper・發動）",
    change: "綁定角色　Viewer → 外包維運",
    docNo: "RQ-20260701-013", result: "已核准；第二關核決：王志明 Overseer",
    source: "人工",
  },
  {
    at: "2026/06/30 16:20", by: "林承翰（Gatekeeper・發動）",
    change: "帳號狀態　啟用中 → 停用",
    docNo: "RQ-20260630-010", result: "已退回",
    reason: "該員仍有 3 筆負責工作未轉派（退回人：王志明 Overseer）",
    source: "人工",
  },
  {
    at: "2026/06/25 09:30", by: "王志明（Overseer）",
    created: "林承翰（EMP-0031）", initialRole: "Viewer",
    docNo: "RQ-20260625-004", result: "已核准", source: "人工",
  },
];

// 其餘使用者也要有歷程 —— 只鋪一個人的話,點到別人就整段消失,
// 會被誤以為是功能壞了(而不是資料沒鋪)。這些是輕量版:建立 + 一筆異動。
const userLog = [
  {
    at: "2026/07/07 15:20", by: "林承翰（Gatekeeper）",
    change: "綁定角色　外包維運 → 稽核查閱",
    docNo: "RQ-20260707-021", result: "已核准", source: "人工",
  },
  {
    at: "2026/07/06 03:00", by: "系統",
    change: "單位名稱　資訊處 → 資訊安全處",
    source: "AD 聯動（免審核）",
  },
  {
    at: "2026/06/30 16:20", by: "林承翰（Gatekeeper・發動）",
    change: "帳號狀態　啟用中 → 停用",
    docNo: "RQ-20260630-010", result: "已退回",
    reason: "該員仍有 3 筆負責工作未轉派（退回人：王志明 Overseer）",
    source: "人工",
  },
];

/** 輕量歷程:建立 + 一筆已核准的異動(給沒有特別劇本的使用者) */
const basicLog = (name, employeeId, role, at, docNo) => [
  {
    at, by: "王志明（Overseer）",
    change: `綁定角色　Viewer → ${role}`,
    docNo, result: "已核准", source: "人工",
  },
  {
    at: "2026/06/25 09:30", by: "王志明（Overseer）",
    created: `${name}（${employeeId}）`, initialRole: "Viewer",
    docNo: "RQ-20260625-004", result: "已核准", source: "人工",
  },
];

// 使用者資訊欄位對準需求書 DCK-01-02(~10 欄含驗證);AD 介接依 8/30 決議「先留欄位」,
// adAccount 由後端 AD 同步維護,前端僅呈現/留位,不接真同步。
export const users = [
  {
    id: 1,
    name: "王大器",
    account: "dachi.wang",
    employeeId: "A001",
    email: "dachi@example.com",
    roleId: 1,
    department: "資訊部",
    title: "資訊部經理",
    officeArea: "02", officePhone: "2345-6789", officeExt: "101",
    mobileCode: "+886", mobile: "912345678",
    phone: "0912-345-678",
    adAccount: "corp\\dachi.wang",
    hireDate: "2018-03-01",
    status: "啟用中",
    approvalStatus: "approved",
    changeLog: userLog,
  },
  {
    id: 2,
    name: "林機房",
    account: "room.lin",
    employeeId: "A002",
    email: "room.lin@example.com",
    roleId: 2,
    department: "維運部",
    title: "機房管理員",
    phone: "0922-333-444",
    adAccount: "corp\\room.lin",
    hireDate: "2020-07-15",
    status: "啟用中",
    approvalStatus: "approved",
    changeLog: basicLog("林機房", "A002", "Wrangler", "2026/06/28 14:10", "RQ-20260628-006"),
  },
  {
    id: 3,
    name: "陳稽核",
    account: "audit.chen",
    employeeId: "A003",
    email: "audit.chen@example.com",
    roleId: 5, // 稽核人員(自訂角色)
    department: "稽核室",
    title: "稽核專員",
    phone: "0933-555-666",
    adAccount: "",
    hireDate: "2021-01-04",
    status: "啟用中",
    approvalStatus: "pending",
    changeLog: basicLog("陳稽核", "A003", "稽核人員", "2026/06/27 09:05", "RQ-20260627-005"),
  },
  // 下面兩位本來就被機櫃/硬體/專案的「管理者」欄位指名,卻不在使用者清單裡 ——
  // 等於懸空的引用。ticket 31 要把管理者補成負責關係,所以先讓他們存在。
  {
    id: 4,
    name: "林承翰",
    account: "ch.lin",
    employeeId: "EMP-0031",
    email: "ch.lin@chainwe.com",
    roleId: 4, // Gatekeeper
    department: "資訊處",
    title: "機房主管",
    phone: "0911-222-333",
    adAccount: "corp\\ch.lin",
    hireDate: "2019-05-06",
    status: "啟用中",
    approvalStatus: "approved",
    // 設計稿(2128:23509)畫的就是這位的歷程
    changeLog: chLinLog,
  },
  {
    id: 5,
    name: "張家豪",
    account: "ch.chang",
    employeeId: "EMP-0052",
    email: "ch.chang@chainwe.com",
    roleId: 2, // Wrangler
    department: "維運部", // 與 db/assets.js 的「張家豪（維運部）」一致
    title: "值班工程師",
    phone: "0955-666-777",
    adAccount: "corp\\ch.chang",
    hireDate: "2022-09-01",
    status: "啟用中",
    approvalStatus: "approved",
    changeLog: basicLog("張家豪", "EMP-0052", "Wrangler", "2026/06/26 16:30", "RQ-20260626-007"),
  },
  // 審核種子 9002 引用 recordId: 34(李宜潔）—— 沒有這筆 user 的話,
  // 審核詳情會找不到對應的「目標紀錄」。
  {
    id: 34,
    name: "李宜潔",
    account: "yj.lee",
    employeeId: "EMP-0134",
    email: "yj.lee@chainwe.com",
    roleId: 3, // Viewer
    department: "維運部",
    title: "維運工程師",
    phone: "0966-888-999",
    adAccount: "corp\\yj.lee",
    hireDate: "2026-06-01",
    status: "啟用中",
    approvalStatus: "pending",
    docNo: "RQ-20260605-012",
    changeLog: basicLog("李宜潔", "EMP-0134", "Viewer", "2026/06/05 15:40", "RQ-20260605-012"),
  },
  // 2026-10-20 SIT 樣本（DcCustomer/GetById/24 原樣，個資已換假）
  // 照抄:單位「資訊處」、職稱「UI」、區碼 02、手機國碼 +886、啟用。
  // 換假:帳號／員編／姓名／email／電話／分機／手機。
  // 角色:SIT role_id 3 是 Wrangler,mock 的 Wrangler 是 2 → roleId 2(role_name 仍是 "Wrangler")。
  {
    id: 35,
    name: "黃柏翰",
    account: "bohan.huang",
    employeeId: "E-001387",
    email: "bohan.huang@example.com",
    roleId: 2, // Wrangler
    department: "資訊處",
    title: "UI",
    officeArea: "02", officePhone: "27001234", officeExt: "215",
    mobileCode: "+886", mobile: "0912000135",
    status: "啟用中",
    approvalStatus: "approved",
    // 異動歷程是 mock-only(custom_properties),SIT 回應沒有;照其他使用者鋪輕量版,點到這位才不會整段消失
    changeLog: basicLog("黃柏翰", "E-001387", "Wrangler", "2026/09/29 10:20", "RQ-20260929-001"),
  },
];

// 工作項目的種子資料改由 db/workItems.js 從資產推導(ticket 31)

// ═══════════ 真後端契約形狀(snake_case)——CustomerRoleDto ═══════════
// ticket 11:與 /api/roles 共用 role store。靜態 customerRoles 僅作初始快照,
// GetAll/GetById 一律走 store 即時投影。

function modulesForRoleRow(row) {
  if (row.levels) return modulesFromLevels(row.levels);
  // 預設角色沒存 levels:給一份最小可用投影(完整矩陣由 UI 端 capabilityMatrix 補)
  if (SYSTEM_ROLE_CODES.has(row.code)) {
    const access = row.code === "Viewer" ? "View" : "Edit";
    return Object.values(PERM_KEY_TO_MODULE_KEY).map((module_key) => ({
      module_key,
      access: ["Decision", "Approval", "Import", "Monitor", "Notify"].includes(module_key)
        && row.code === "Viewer"
        ? "Hidden"
        : module_key === "Decision" && row.code === "Wrangler"
          ? "Hidden"
          : access,
    }));
  }
  return Object.values(PERM_KEY_TO_MODULE_KEY).map((module_key) => ({
    module_key, access: "Hidden",
  }));
}

function fieldsForRoleRow(row) {
  if (row.masks) return fieldsFromMasks(row.masks);
  const canSee = row.code === "Overseer" || row.code === "Gatekeeper";
  return [
    { field_key: "ContractAmount", enabled: canSee },
    { field_key: "AssetAmount", enabled: canSee },
    { field_key: "ReportAmount", enabled: canSee },
    { field_key: "DecisionAmount", enabled: canSee },
  ];
}

/**
 * role store 列 → CustomerRoleDto。
 * usersList 用來算 bound_customer_count(與刪除阻擋同一資料源)。
 */
export function roleRowToCustomerRole(row, usersList = users) {
  const system = SYSTEM_ROLE_CODES.has(row.code);
  return {
    id: row.id,
    name: row.name,
    description: row.description || row.scope || "",
    active: row.status !== "停用",
    is_system_role: system,
    system_name: row.code,
    role_type_name: system ? "預設" : "自訂",
    bound_customer_count: usersList.filter((u) => String(u.roleId) === String(row.id)).length,
    last_modified_on: row.updatedAt || "2026-08-01T09:00:00Z",
    last_modified_by_customer_id: null,
    last_modified_by_name: row.updatedBy || "系統",
    is_under_review: row.approvalStatus === "pending",
    approval_request_no: row.docNo || null,
    modules: modulesForRoleRow(row),
    fields: fieldsForRoleRow(row),
    // ⚠️ 契約外:異動歷程／範圍文案,WF 詳情需要;hybrid 缺席時畫面降級
    custom_properties: {
      scope: row.scope || "",
      change_log: row.changeLog || [],
    },
  };
}

/** Create/Update body → role store 列(camelCase);審核引擎 patchTarget 也吃這份 */
export function customerRolePatchToRoleRow(dto) {
  if (!dto) return dto;
  const out = {};
  if (dto.name !== undefined) out.name = dto.name;
  if (dto.description !== undefined) out.description = dto.description;
  if (dto.system_name !== undefined) out.code = dto.system_name;
  if (dto.active !== undefined) out.status = dto.active ? "啟用中" : "停用";
  if (dto.modules) out.levels = levelsFromModules(dto.modules);
  if (dto.fields) out.masks = masksFromFields(dto.fields);
  const cp = dto.custom_properties || {};
  if (cp.scope !== undefined) out.scope = cp.scope;
  if (cp.change_log !== undefined) out.changeLog = cp.change_log;
  return out;
}

// 靜態快照(相容舊測試引 export);執行期以 roleStore 投影為準
export const customerRoles = roles.map((r) => roleRowToCustomerRole(r, users));

// —— 使用者 row(camelCase,mock 內部儲存) ⇄ 後端契約 DcCustomer(snake_case) ——
// mock 是「真後端替身」:內部沿用既有 user store(與審核引擎共用一份資料),
// wire 形狀依 Backend v1.00 契約。契約沒有的 WF 欄位(公司/分公司/公司電話國碼/
// 帳號狀態/AD 帳號/到職日/異動歷程)以 custom_properties 傳遞——文件的
// ChangePasswordModelDto 等已有此慣例;⚠️ 正式欄位待與後端確認。

export function userRowToDcCustomer(row) {
  const role = roles.find((r) => r.id === row.roleId);
  return {
    username: row.account || "",
    employee_no: row.employeeId || "",
    name: row.name || "",
    email: row.email || "",
    phone_area_code: row.officeArea || "",
    phone: row.officePhone || "",
    extension: row.officeExt || "",
    mobile_country_code: row.mobileCode || "+886",
    mobile_phone: row.mobile || "",
    password: "",
    confirm_password: "",
    unit_name: row.department || "",
    job_title: row.title || "",
    role_id: row.roleId || 0,
    role_name: role ? role.name : "",
    active: row.status !== "停用",
    id: row.id,
    custom_properties: {
      office_country_code: row.officeCode || "+886",
      company: row.company || "",
      branch_office: row.branchOffice || "",
      status: row.status || "",
      ad_account: row.adAccount || "",
      hire_date: row.hireDate || "",
      must_change_password: !!row.mustChangePassword, // 首登改密流程(ticket 34)

      approval_status: row.approvalStatus || "",
      doc_no: row.docNo || "",
      change_log: row.changeLog || [],
    },
  };
}

// GetAll 投影:DcCustomerListItemDto。phone 含區碼(顯示格式同 WF「(02)2345-6789」)。
export function userRowToDcCustomerListItem(row) {
  const dto = userRowToDcCustomer(row);
  // WF 260804(2128:22709):電話欄「國碼-區碼-號碼#分機」
  const phoneMain = [row.officeCode || "+886", row.officeArea, row.officePhone || row.phone]
    .filter(Boolean).join("-");
  return {
    username: dto.username, name: dto.name, employee_no: dto.employee_no,
    unit_name: dto.unit_name, job_title: dto.job_title,
    role_id: dto.role_id, role_name: dto.role_name, email: dto.email,
    phone: phoneMain, extension: dto.extension,
    // 手機兩欄刻意**不**放列表項:真後端 DcCustomerListItemDto 沒有,而 8/27 起列表
    // 第 6 欄是「電話#分機」,手機只在詳情/表單(單筆 DcCustomerDto 有)。9/13 拿掉。
    active: dto.active,
    status: row.approvalStatus === "pending" ? "審核中" : row.status === "停用" ? "停用" : "啟用中",
    is_under_review: row.approvalStatus === "pending",
    approval_request_no: row.docNo || null,
    id: row.id,
  };
}

// 契約 DTO/patch(snake_case) → user row(camelCase)。
// Create/Update 端點與審核核准套用(approvals patchTarget)共用,確保兩邊寫法一致。
export function dcCustomerPatchToUserRow(patch) {
  if (!patch) return patch;
  const cp = patch.custom_properties || {};
  const KEY_MAP = {
    username: "account", employee_no: "employeeId", name: "name", email: "email",
    phone_area_code: "officeArea", phone: "officePhone", extension: "officeExt",
    mobile_country_code: "mobileCode", mobile_phone: "mobile",
    unit_name: "department", job_title: "title",
  };
  const CP_MAP = {
    office_country_code: "officeCode", company: "company", branch_office: "branchOffice",
    status: "status", ad_account: "adAccount", hire_date: "hireDate",
  };
  const out = {};
  Object.entries(patch).forEach(([k, v]) => {
    if (k === "role_id") { out.roleId = Number(v) || v; return; }
    if (KEY_MAP[k] !== undefined) out[KEY_MAP[k]] = v;
    // password/confirm_password/active/role_name/id/custom_properties:不落 row(密碼 mock 不保存)
  });
  Object.entries(cp).forEach(([k, v]) => { if (CP_MAP[k] !== undefined) out[CP_MAP[k]] = v; });
  return out;
}
